/**
 * The job's top box — "20 passed · 2 failed · 1 couldn't do" — drawn the same
 * way on the office's new job page and on the tech's iPad job screen
 * (threads/job-page-fresh, Corbin 10/28: "the office page and the tech's job
 * screen must feel seamless"). The words come from the job type
 * (`GET /jobs/:id/outcomes`); each device's state from the job page's device
 * rule (calm/calmModel.classifyDevice → fresh/freshModel.itemStateOf).
 */
import { cn } from "@/lib/utils";
import {
    outcomeFill,
    outcomeInk,
    tallyOutcomes,
    useJobOutcomes,
    type OutcomeSet,
    type OutcomeTally,
} from "@/lib/job-outcomes";
import { buildDevices } from "@/pages/jobs/detail/calm/calmModel";
import type { JobAssetRow, JobRepairResult, JobTestResult } from "@/pages/jobs/detail/types";
import { itemStateOf } from "@/pages/jobs/fresh/freshModel";

/** The numbers and the bar. `compact` = the iPad header. */
export function OutcomeNumbers({
    tally,
    set,
    compact = false,
    testIdPrefix = "fresh-count",
}: {
    tally: OutcomeTally;
    set: OutcomeSet;
    compact?: boolean;
    testIdPrefix?: string;
}) {
    const big = compact ? "text-[30px]" : "text-[44px]";
    if (tally.before) {
        return (
            <div className="mt-2" data-testid={`${testIdPrefix}-before`}>
                <span className={cn(big, "font-extrabold leading-none tabular-nums")}>{tally.total}</span>{" "}
                <span className="text-[16px] font-bold">{set.before}</span>
            </div>
        );
    }
    const sum = tally.counts.reduce((a, c) => a + c.n, 0);
    return (
        <>
            <div className={cn("flex flex-wrap gap-y-1", compact ? "mt-1 gap-x-4" : "mt-2 gap-x-5")}>
                {tally.counts.map((c) => (
                    <div key={c.key} className="grid" data-testid={`${testIdPrefix}-${c.key}`}>
                        <span className={cn(big, "font-extrabold leading-none tabular-nums", outcomeInk(c.color, c.n))}>
                            {c.n}
                        </span>
                        <span className={compact ? "text-[13px] font-bold" : "text-[15px] font-bold"}>{c.label}</span>
                    </div>
                ))}
            </div>
            {sum > 0 && !compact && (
                <div className="mt-3 flex h-3 gap-0.5 overflow-hidden rounded-md bg-muted" aria-hidden>
                    {tally.counts
                        .filter((c) => c.n > 0)
                        .map((c) => (
                            <i key={c.key} className={cn("block", outcomeFill(c.color))} style={{ flex: c.n }} />
                        ))}
                </div>
            )}
        </>
    );
}

/** The whole box for a job, from GET /api/jobs/:id's own data (the iPad's header). */
export function JobOutcomeBox({
    jobId,
    resp,
    preVisit,
}: {
    jobId: string;
    resp: { assets?: unknown[]; testResults?: unknown[]; repairResults?: unknown[] } | null | undefined;
    preVisit: boolean;
}) {
    const words = useJobOutcomes(jobId);
    if (!resp || !words.data) return null;
    const devices = buildDevices(
        (resp.assets ?? []) as JobAssetRow[],
        (resp.testResults ?? []) as JobTestResult[],
        (resp.repairResults ?? []) as JobRepairResult[],
        !preVisit,
    );
    if (devices.length === 0) return null;
    const tally = tallyOutcomes(words.data, devices.map(itemStateOf), preVisit);
    return (
        <div
            className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-stone-900"
            data-testid="job-outcome-box"
            aria-label="How the job is going"
        >
            <OutcomeNumbers tally={tally} set={words.data} compact testIdPrefix="ipad-count" />
        </div>
    );
}
